import React, { useState, useEffect } from 'react';
import {
  Container, Box, Typography, TextField, CircularProgress,
  Alert, Paper, Tabs, Tab, Chip, Divider, Select, MenuItem, FormControl,
  InputLabel,
} from '@mui/material';
import axios from 'axios';
import ProductImageGenerator from '../components/AIImageGenerator/ProductImageGenerator';

const AIImageGeneratorPage = ({ storeId }) => {
  const [activeTab, setActiveTab] = useState(0); // 0 = Imported, 1 = Existing, 2 = Custom
  const [importedProducts, setImportedProducts] = useState([]);
  const [shopifyProducts, setShopifyProducts] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState(storeId || '');
  const [stores, setStores] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Custom mode state
  const [customTitle, setCustomTitle] = useState('');
  const [customImageUrl, setCustomImageUrl] = useState('');

  useEffect(() => {
    fetchStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedStoreId) {
      if (activeTab === 0) fetchImportedProducts();
      if (activeTab === 1) fetchShopifyProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStoreId, activeTab]);

  const fetchStores = async () => {
    try {
      const res = await axios.get('/api/stores');
      setStores(res.data || []);
      if (res.data?.length > 0 && !selectedStoreId) {
        setSelectedStoreId(res.data[0].id);
      }
    } catch {
      // silently handle — user may not be authenticated
    }
  };

  const fetchImportedProducts = async () => {
    if (!selectedStoreId) return;
    setLoadingProducts(true);
    setErrorMsg('');
    try {
      const res = await axios.get(`/api/import/imported-products/${selectedStoreId}`);
      setImportedProducts(res.data || []);
    } catch (err) {
      setErrorMsg('Failed to load imported products: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchShopifyProducts = async () => {
    if (!selectedStoreId) return;
    setLoadingProducts(true);
    setErrorMsg('');
    try {
      const res = await axios.get(`/api/image-generate/shopify-products/${selectedStoreId}`);
      setShopifyProducts(res.data?.products || []);
    } catch (err) {
      setErrorMsg('Failed to load Shopify products: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoadingProducts(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          AI Image Generation
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Generate professional e-commerce product images using AI for imported or existing Shopify products.
        </Typography>

        {/* Store selector */}
        {stores.length > 1 && (
          <FormControl sx={{ mb: 3, minWidth: 260 }}>
            <InputLabel>Select Store</InputLabel>
            <Select
              value={selectedStoreId}
              label="Select Store"
              onChange={(e) => setSelectedStoreId(e.target.value)}
            >
              {stores.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.storeName || s.storeIdentifier}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
          <Tab label="Imported Products" />
          <Tab label="Existing Shopify Products" />
          <Tab label="Custom / Manual" />
        </Tabs>

        {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

        {/* Tab 0: Imported products */}
        {activeTab === 0 && (
          <Box>
            {loadingProducts ? (
              <CircularProgress />
            ) : importedProducts.length === 0 ? (
              <Alert severity="info">No imported products found for this store.</Alert>
            ) : (
              importedProducts.map((product) => (
                <Paper key={product.id} elevation={1} sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    {product.imageUrls?.[0] && (
                      <img
                        src={product.imageUrls[0]}
                        alt={product.title}
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }}
                      />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>{product.title}</Typography>
                      {product.price && (
                        <Chip label={`¥${product.price}`} size="small" sx={{ mb: 1 }} />
                      )}
                      <Divider sx={{ my: 1 }} />
                      <ProductImageGenerator
                        productTitle={product.title}
                        imageUrls={product.imageUrls || []}
                        storeId={selectedStoreId}
                      />
                    </Box>
                  </Box>
                </Paper>
              ))
            )}
          </Box>
        )}

        {/* Tab 1: Existing Shopify products */}
        {activeTab === 1 && (
          <Box>
            {loadingProducts ? (
              <CircularProgress />
            ) : shopifyProducts.length === 0 ? (
              <Alert severity="info">No products found in this Shopify store, or store not connected.</Alert>
            ) : (
              shopifyProducts.map((product) => {
                const imageUrls = (product.images || []).map((img) => img.src).filter(Boolean);
                return (
                  <Paper key={product.id} elevation={1} sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      {imageUrls[0] && (
                        <img
                          src={imageUrls[0]}
                          alt={product.title}
                          style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }}
                        />
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>{product.title}</Typography>
                        <Divider sx={{ my: 1 }} />
                        <ProductImageGenerator
                          productTitle={product.title}
                          imageUrls={imageUrls}
                          storeId={selectedStoreId}
                        />
                      </Box>
                    </Box>
                  </Paper>
                );
              })
            )}
          </Box>
        )}

        {/* Tab 2: Custom / manual */}
        {activeTab === 2 && (
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Generate image for any product
            </Typography>
            <TextField
              label="Product Title"
              fullWidth
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="e.g. Ceramic Tea Set, Traditional Style"
            />
            <TextField
              label="Reference Image URL (optional)"
              fullWidth
              value={customImageUrl}
              onChange={(e) => setCustomImageUrl(e.target.value)}
              sx={{ mb: 3 }}
              placeholder="https://example.com/reference.jpg"
            />
            <ProductImageGenerator
              productTitle={customTitle}
              imageUrls={customImageUrl ? [customImageUrl] : []}
              storeId={selectedStoreId}
            />
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default AIImageGeneratorPage;
